import { useEffect, useState } from "react";

import { CircularProgressCenter } from "igfd-ui/src/app-index";

import {
  localStorageAccessor,
  useResolveMenuUrl,
} from "igfd-commons/src/app-index";

import { config } from "../environment/config";
import { STAFF_LOCATION_ACCESS_STORAGE_KEY } from "../environment/local-storage-keys";
import { client } from "../queries/axios-client";
import { NotFoundPage } from "./errors/NotFoundPage";
import { RestaurantProvider } from "./restaurant-provider";
import { StaffLoginPage } from "./staff-login-page/staff-login-page";
import { WelcomePage } from "./welcome-page/welcome-page";

/**
 * This component is rendered first right after the app is loaded,
 * It is responsible for figuring out everything from the URL and then proceeding to the menu app component.
 * mui theme is default at this stage.
 */
export const RestaurantStaffApp = () => {
  const [authenticated, isAuthenticated] = useState(false);
  const {
    loading: urlResolutionLoading,
    restaurantData,
    urlSlug,
  } = useResolveMenuUrl({
    client,
  });

  // TODO: This access key should be based on the urlSlug, not locationId.
  // The staff PIN (hashedStaffAppPIN) is per menu URL, not per location, in our V2 model.
  // However, changing the key will log out all currently authenticated staff
  // (their localStorage entries use the old key). We need to coordinate with
  // restaurants before switching. For now, we use locations[0] as an intermediate
  // step to remove the legacy singular `location` field from the response.
  const accessId =
    restaurantData?.locations?.[0]?.id || restaurantData?.restaurant.uuid;
  const [getLocationAccess, setLocationAccess] = localStorageAccessor<{
    accessIds: string[];
  } | null>(STAFF_LOCATION_ACCESS_STORAGE_KEY, null);

  useEffect(() => {
    if (accessId) {
      const items = getLocationAccess();
      isAuthenticated(
        !!items?.accessIds && items?.accessIds.includes(accessId)
      );
    }
  }, [accessId]);

  const handleAccess = (access: boolean) => {
    if (!accessId) {
      return;
    }
    const items = getLocationAccess();

    if (items?.accessIds) {
      const accessIds = access
        ? [...new Set([...items.accessIds, accessId])]
        : items.accessIds.filter(
            (id) => id !== restaurantData?.locations?.[0]?.id
          );
      setLocationAccess({ accessIds });
    } else if (access) {
      setLocationAccess({ accessIds: [accessId] });
    }
    isAuthenticated(access);
  };

  if (urlResolutionLoading) {
    return <CircularProgressCenter />;
  }

  if (!restaurantData) {
    return <NotFoundPage />;
  }

  return (
    <RestaurantProvider restaurantData={restaurantData} urlSlug={urlSlug}>
      {!authenticated && !config.isPrPreview ? (
        <StaffLoginPage onAuthenticate={() => handleAccess(true)} />
      ) : (
        <WelcomePage onSignOut={() => handleAccess(false)} />
      )}
    </RestaurantProvider>
  );
};
